import { Box, CircularProgress } from "@mui/material"
import { Outlet, createRootRoute, useBlocker, useLocation } from "@tanstack/react-router"

import { CancelDialog } from "@/components/common/Modals"
import { useBackgroundTokenCheck } from "@/hooks/useBackgroundTokenCheck"
import { useAuthStore } from "@/stores/authStore"
import { useNavigationGuardStore } from "@/stores/useNavigationGuardStore"
import theme from "@/styles/mui-theme"
import { checkAuthStatus, isPublicRoute } from "@/utils/auth"

export const Route = createRootRoute({
  component: RootComponent,
  // Verify auth status before any route is rendered
  beforeLoad: async ({ location }) => {
    const store = useAuthStore.getState()

    // Skip auth check for public routes
    if (isPublicRoute(location.pathname)) {
      return null
    }

    // For all other routes, check auth status
    const isValid = await checkAuthStatus()
    if (!isValid) {
      store.signIn() // Redirect to Cognito if not authenticated
      return null
    }

    return null
  },
})

function RootComponent() {
  const { status } = useAuthStore()
  const location = useLocation()
  const isPublic = isPublicRoute(location.pathname)

  useBackgroundTokenCheck()

  const {
    status: blockerStatus,
    proceed,
    reset,
  } = useBlocker({
    shouldBlockFn: ({ current, next }) => {
      if (!useNavigationGuardStore.getState().shouldBlock || useAuthStore.getState().status !== "authenticated")
        return false
      return next.pathname !== current.pathname
    },
    withResolver: true,
    enableBeforeUnload: () =>
      useNavigationGuardStore.getState().shouldBlock && useAuthStore.getState().status === "authenticated",
  })

  // Only show loading for initial auth check
  if (status === "loading") {
    return (
      <Box className='flex h-screen items-center justify-center'>
        <CircularProgress
          size={48}
          className='transition-opacity duration-300 ease-in-out'
          sx={{
            color: theme.palette.primary.main,
          }}
        />
      </Box>
    )
  }

  // Public routes don't show the app shell
  if (isPublic) {
    return <Outlet />
  }

  return (
    <div className='flex h-screen bg-gray-100'>
      <Outlet />
      {blockerStatus === "blocked" && (
        <CancelDialog
          onClose={() => reset?.()}
          onConfirm={() => {
            useNavigationGuardStore.getState().onNavigationConfirmHandler?.()
            proceed?.()
          }}
        />
      )}
    </div>
  )
}
